"use client"

import { useEffect, useRef } from "react"
import { useFoldersStore } from "@/lib/folders-store"
import { useAuthStore } from "@/lib/auth-store"

const MIGRATION_FLAG_KEY = "goonru:folders-migrated-to-goonfolio"

/** One-time client-side migration of the old localStorage-only "folders"
 * bookmarking system into real Goonfolio collections. Folders were always
 * private/local, so they migrate in as private folios (owner can make them
 * public afterward) — this only runs once per browser, then clears the old
 * store so the legacy long-press "add to folder" flow has nothing left to
 * point at. Call from a component a logged-in user actually visits (the
 * Goonfolio tab), not globally, so it doesn't fire on every page load. */
export function useMigrateLegacyFolders(enabled: boolean, onMigrated?: () => void) {
  const { user } = useAuthStore()
  const ran = useRef(false)

  useEffect(() => {
    if (!enabled || ran.current || !user) return
    if (typeof window === "undefined") return
    if (localStorage.getItem(MIGRATION_FLAG_KEY)) return

    const folders = Array.from(useFoldersStore.getState().folders.values())
    if (folders.length === 0) {
      localStorage.setItem(MIGRATION_FLAG_KEY, "1")
      return
    }

    ran.current = true

    ;(async () => {
      for (const folder of folders) {
        if (folder.postIds.size === 0) continue
        try {
          const createRes = await fetch("/api/collections", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ name: folder.name, description: folder.description ?? "", isPublic: false }),
          })
          if (!createRes.ok) continue
          const { collection } = await createRes.json()

          for (const postId of folder.postIds) {
            const post = folder.posts.get(postId)
            if (!post) continue
            await fetch(`/api/collections/${collection.id}/items`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ itemType: "external_content", externalContent: post }),
            }).catch(() => {})
          }
        } catch {
          // Best-effort — a failed folder just stays in local storage for next time.
        }
      }

      localStorage.setItem(MIGRATION_FLAG_KEY, "1")
      for (const folder of folders) {
        useFoldersStore.getState().deleteFolder(folder.id)
      }
      onMigrated?.()
    })()
  }, [enabled, user, onMigrated])
}
